import { useEffect } from "react";
import { useParams, Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft,
  ArrowRight,
  Users,
  Pill,
  CalendarClock,
  CheckCircle2,
  BookOpen,
  Download,
  FlaskConical,
  TrendingUp,
} from "lucide-react";
import { Reveal } from "../components/Reveal";
import { INGREDIENTS } from "../data/content";

const STUDY_META = [
  { key: "population", label: "Study Population", icon: Users },
  { key: "dosage", label: "Dosage", icon: Pill },
  { key: "duration", label: "Study Duration", icon: CalendarClock },
];

export default function ResearchDetail() {
  const { slug } = useParams();
  const index = INGREDIENTS.findIndex((i) => i.slug === slug);
  const ing = INGREDIENTS[index];

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  if (!ing) return <Navigate to="/" replace />;
  const next = INGREDIENTS[(index + 1) % INGREDIENTS.length];

  return (
    <main data-testid="research-detail-page" className="bg-white pt-[72px]">
      {/* Hero band */}
      <section className="relative overflow-hidden bg-gradient-to-br from-fv-ice to-fv-mist py-20 lg:py-28">
        <div className="pointer-events-none absolute -right-32 -top-32 h-96 w-96 rounded-full bg-fv-blue/10 blur-3xl" />
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <Link
            to="/#research"
            data-testid="back-to-research-link"
            className="group inline-flex items-center gap-2 text-xs font-bold tracking-[0.15em] text-fv-navy/60 transition-colors duration-300 hover:text-fv-blue"
          >
            <ArrowLeft className="h-4 w-4 transition-transform duration-300 group-hover:-translate-x-1" />
            BACK TO RESEARCH
          </Link>

          <div className="mt-10 flex flex-col items-start gap-10 md:flex-row md:items-center">
            <motion.div
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
              className="relative shrink-0"
            >
              <div className="absolute -inset-3 rounded-full border border-dashed border-fv-blue/40" />
              <img
                src={ing.image}
                alt={ing.name}
                className="h-40 w-40 rounded-full border-4 border-white object-cover shadow-2xl shadow-fv-blue/20 md:h-52 md:w-52"
                data-testid="ingredient-image"
              />
            </motion.div>
            <div>
              <span className="font-display text-sm font-bold tracking-[0.25em] text-fv-blue">
                INGREDIENT {ing.num} / 04
              </span>
              <div className="mt-2 overflow-hidden">
                <motion.h1
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.8, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
                  className="font-display text-4xl font-extrabold tracking-tight text-fv-navy sm:text-5xl"
                  data-testid="ingredient-name"
                >
                  {ing.name}
                </motion.h1>
              </div>
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4, duration: 0.7 }}
                className="mt-5 max-w-xl text-base leading-relaxed text-fv-navy/60 md:text-lg"
              >
                {ing.short}
              </motion.p>
              {ing.patentLogo && (
                <motion.div
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.55, duration: 0.6 }}
                  className="mt-6 inline-flex items-center rounded-xl border border-fv-blue/20 bg-white px-5 py-2.5 shadow-lg shadow-fv-blue/5"
                >
                  <img
                    src={ing.patentLogo}
                    alt={`${ing.name} patented brand logo`}
                    className="h-8 w-auto object-contain"
                    data-testid="patent-logo"
                  />
                </motion.div>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Body */}
      <section className="py-20 lg:py-28">
        <div className="mx-auto max-w-5xl px-5 lg:px-8">
          <div className="grid gap-16 lg:grid-cols-[1fr_320px]">
            <div className="space-y-16">
              <Reveal>
                <div>
                  <h2 className="flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-fv-navy">
                    <span className="h-5 w-1 rounded-full bg-fv-blue" />
                    Ingredient Overview
                  </h2>
                  <p className="mt-5 leading-relaxed text-fv-navy/70" data-testid="ingredient-overview">
                    {ing.overview}
                  </p>
                </div>
              </Reveal>

              <Reveal>
                <div>
                  <h2 className="flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-fv-navy">
                    <span className="h-5 w-1 rounded-full bg-fv-blue" />
                    Mechanism of Action
                  </h2>
                  <p className="mt-5 leading-relaxed text-fv-navy/70" data-testid="ingredient-mechanism">
                    {ing.mechanism}
                  </p>
                </div>
              </Reveal>

              <Reveal>
                <div>
                  <h2 className="flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-fv-navy">
                    <span className="h-5 w-1 rounded-full bg-fv-blue" />
                    Key Results
                  </h2>
                  <ul className="mt-6 space-y-4" data-testid="ingredient-results">
                    {ing.results.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 text-sm leading-relaxed text-fv-navy/70 md:text-base">
                        <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-fv-blue" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>

              {ing.studies && (
                <Reveal>
                  <div>
                    <h2 className="flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-fv-navy">
                      <span className="h-5 w-1 rounded-full bg-fv-blue" />
                      Clinical Studies
                    </h2>
                    <div className="mt-6 space-y-5" data-testid="clinical-studies">
                      {ing.studies.map((s, i) => (
                        <div
                          key={i}
                          data-testid={`clinical-study-${i}`}
                          className="group rounded-3xl border border-blue-100 bg-white p-6 shadow-sm transition-all duration-500 hover:-translate-y-1 hover:border-fv-blue/40 hover:shadow-xl hover:shadow-fv-blue/10 md:p-7"
                        >
                          <div className="flex items-start gap-4">
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-fv-ice">
                              <FlaskConical className="h-5 w-5 text-fv-blue" strokeWidth={1.8} />
                            </div>
                            <div className="min-w-0">
                              <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-fv-blue">
                                Study {i + 1}
                              </div>
                              <h3 className="mt-1 font-display text-base font-bold leading-snug text-fv-navy">
                                {s.objective}
                              </h3>
                            </div>
                          </div>
                          <div className="mt-5 grid gap-3 sm:grid-cols-2">
                            <div className="flex items-center gap-3 rounded-xl bg-fv-ice/60 px-4 py-3">
                              <Users className="h-4 w-4 shrink-0 text-fv-blue" />
                              <span className="text-xs font-semibold text-fv-navy/75">{s.subjects}</span>
                            </div>
                            <div className="flex items-center gap-3 rounded-xl bg-fv-ice/60 px-4 py-3">
                              <Pill className="h-4 w-4 shrink-0 text-fv-blue" />
                              <span className="text-xs font-semibold text-fv-navy/75">{s.doseDuration}</span>
                            </div>
                          </div>
                          <ul className="mt-5 space-y-2.5">
                            {s.outcomes.map((o, j) => (
                              <li key={j} className="flex items-start gap-2.5 text-sm leading-relaxed text-fv-navy/70">
                                <TrendingUp className="mt-0.5 h-4 w-4 shrink-0 text-fv-blue" />
                                {o}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-5 border-t border-blue-50 pt-4 text-xs italic leading-relaxed text-fv-navy/50">
                            {s.reference}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                </Reveal>
              )}

              <Reveal>
                <div>
                  <h2 className="flex items-center gap-3 font-display text-xl font-extrabold tracking-tight text-fv-navy">
                    <span className="h-5 w-1 rounded-full bg-fv-blue" />
                    Scientific References
                  </h2>
                  <ul className="mt-6 space-y-4" data-testid="ingredient-references">
                    {ing.references.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 rounded-2xl border border-blue-100 bg-fv-ice/60 p-5 text-sm leading-relaxed text-fv-navy/70">
                        <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-fv-blue" />
                        {r}
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            </div>

            {/* Sidebar */}
            <aside className="space-y-6 lg:sticky lg:top-28 lg:self-start">
              <Reveal delay={0.1}>
                <div className="rounded-3xl border border-blue-100 bg-white p-7 shadow-lg shadow-fv-blue/5" data-testid="study-summary-card">
                  <h3 className="font-display text-sm font-extrabold tracking-[0.15em] text-fv-navy">
                    CLINICAL STUDY SUMMARY
                  </h3>
                  <p className="mt-4 text-sm leading-relaxed text-fv-navy/60">
                    {ing.study.summary}
                  </p>
                  <div className="mt-6 space-y-4 border-t border-blue-50 pt-6">
                    {STUDY_META.map(({ key, label, icon: Icon }) => (
                      <div key={key} className="flex items-start gap-3">
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-fv-ice">
                          <Icon className="h-4 w-4 text-fv-blue" strokeWidth={1.8} />
                        </div>
                        <div>
                          <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-fv-navy/45">
                            {label}
                          </div>
                          <div className="mt-0.5 text-sm font-semibold text-fv-navy">
                            {ing.study[key]}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button
                    data-testid="download-research-btn"
                    onClick={() => toast.info("Full research papers will be available for download soon.")}
                    className="mt-7 flex w-full items-center justify-center gap-2 rounded-full bg-fv-blue py-3.5 text-xs font-bold tracking-[0.12em] text-white shadow-lg shadow-fv-blue/30 transition-all duration-300 hover:bg-fv-navy"
                  >
                    <Download className="h-4 w-4" />
                    VIEW RESEARCH PAPER
                  </button>
                </div>
              </Reveal>
            </aside>
          </div>

          {/* Next ingredient */}
          <Reveal className="mt-24">
            <Link
              to={`/research/${next.slug}`}
              data-testid="next-ingredient-link"
              className="group flex items-center justify-between gap-6 rounded-3xl bg-fv-navy p-8 transition-all duration-500 hover:shadow-2xl hover:shadow-fv-navy/30 md:p-10"
            >
              <div>
                <div className="text-[11px] font-bold tracking-[0.25em] text-fv-cyan">
                  NEXT INGREDIENT
                </div>
                <div className="mt-2 font-display text-xl font-extrabold text-white md:text-2xl">
                  {next.name}
                </div>
              </div>
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-white/20 text-white transition-all duration-300 group-hover:bg-fv-blue group-hover:border-fv-blue">
                <ArrowRight className="h-5 w-5 transition-transform duration-300 group-hover:translate-x-1" />
              </div>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}
